import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject, ReactNode } from "react";
import { CodeRunSummary } from "../../components/CodeRunSummary.js";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AuthenticityBand,
  Candidacy,
  CandidacyNote,
  DecisionRecord,
  EngagementType,
  IntegrityEventKind,
  IntegritySignal,
  InterviewMode,
  PipelineStatus,
  PresenceBasis,
  QuestionKind,
  RankingBasis,
  WorkflowConfig,
} from "@talenthop/shared";
import {
  OPTION_LETTERS,
  PIPELINE_STATUSES,
  PIPELINE_STATUS_LABEL,
  STRICT_PRESENCE_WEIGHT,
  interviewModeOf,
  parsePresenceReturn,
} from "@talenthop/shared";
import { INTEGRITY_EVENT_LABEL, INTEGRITY_KIND_ORDER, INTEGRITY_PHRASE, bypassRequirementLabel, humaneRecommendation, integrityEventDetail, parseStrictResume, strictReasonLabel } from "./integrityLabels.js";
import { decisionErrorMessage, settledDecision } from "./decisionState.js";
import { pipelineStatusPillClass, PROMPT_EDITOR_ROLES } from "./pipeline.js";
import { adaptiveLabel, provenanceHeadline, sourceChip, sourceQuote } from "./questionProvenance.js";
import { formatClock, recordingCoverage, spanSeconds, type RecordingPart } from "./recordingCoverage.js";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { planBadge } from "./CandidacyQuestionsPanel.js";
import { prettyId } from "./useVerticals.js";
import { languageFullLabel } from "./interviewLanguages.js";
import { LanguagePill } from "./LanguagesEditor.js";
import {
  ShieldCheck,
  Check,
  CheckBig,
  Alert,
  Clock,
  Copy,
  Eye,
  Info,
  Arrow,
  Video,
  Bolt,
  Users,
  RefreshCw,
  Mail,
  Phone,
  FileText,
  Send,
} from "../../components/icons.js";
import { engagementLabel, engagementPillClass } from "./engagement.js";
import { ResumeProfileLines, hasResumeProfile, type ResumeProfileData } from "./ResumeProfileView.js";
import { InterviewAssistant } from "./InterviewAssistant.js";
import { ConductObservations } from "./ConductObservations.js";
import { ResumeProfileEditor } from "./ResumeProfileEditor.js";
import { CandidateDetailsFields } from "./network/CandidateDetailsFields.js";
import type { ResumeOverrideKey } from "@talenthop/shared";
import { InterviewScorePanel, type InterviewScoreData } from "./InterviewScorePanel.js";
import { ScrollText } from "lucide-react";
import { CandidateTimeline } from "../../components/CandidateTimeline.js";
import { ConfirmChargeModal } from "./ConfirmChargeModal.js";
import { useReportSource } from "./reportSource.js";
import { ResumeMatchExplain, type ResumeMatchView } from "./ResumeMatchExplain.js";
import { CvAddReplace } from "./CvAddReplace.js";
import {
  matchMetaOf,
  verdictOf,
  CapabilityCoverage,
  CompetencyEvidenceList,
  EvaluationHistory,
  EvaluationNotices,
  ReEvaluateControl,
  ResumeRescoreControl,
  ResumeScoreControl,
  type EvaluationSummary,
  type ReportAssessment,
  type ReportEvaluation,
} from "./EvaluationPanel.js";

interface ReportTranscriptItem {
  seq: number;
  kind: QuestionKind;
  prompt: string;
  answerText: string | null;
  /** 0063 — the recording-verified answer. When present for a voice answer, THE JUDGE SCORED
   *  THIS TEXT, so the report shows it as the answer; captureLossPct is how much of it the live
   *  browser capture had missed; answeredBy 'voice_edited' marks a candidate-corrected caption. */
  verifiedText?: string | null;
  captureLossPct?: number | null;
  answeredBy?: string | null;
  codeResult: unknown | null;
  /** Snippet choice (#78) — all four null/absent on questions that carried no options. The pick's
   *  correctness was decided deterministically at submission, never by the judge. */
  options?: string[] | null;
  correctIndex?: number | null;
  pickedIndex?: number | null;
  pickCorrect?: boolean | null;
  /** About where this answer sits in the recording, in seconds from the session start (null when unknown). */
  timing?: { startSec: number; endSec: number } | null;
  /** Provenance (this wave) — which rubric line the question probes, where it was DRAWN from
   *  (requirement / resume / standard set / composed live), and the phrase the API VERIFIED
   *  appears in that source. All nullable/absent on a pre-wave interview or an older API payload:
   *  no chip renders, which is the only honest rendering of "no claim was stored". */
  competencyId?: string | null;
  source?: "requirement" | "resume" | "standard" | "adaptive" | null;
  sourcePhrase?: string | null;
  /** Per-answer score + feedback from the CURRENT evaluation (0099). Both null = not evaluated
   *  (blank answer, or no successful evaluation yet) — never a fabricated percentage. */
  /** What this answer evidenced — derived from the competency results (0100), never a score of its own. */
  evidence?: { competencyId: string; label: string; strength: string; span: string }[];
  /** The competencies the evaluator found evidence for in THIS answer — one answer can evidence
   *  several. Labels resolve from the evaluation's competencies. */
  evidenceFor?: string[];
  /** This answer's own question-level score (0105): from the role and this question alone — never a
   *  competency score. Absent until the interview has been question-scored. */
  questionScore?: { score: number | null; reason: string; span: string | null; source: string; counts: boolean } | null;
  /** WS4 — a non-English interview. `promptLocalized` is what Asha actually asked (`prompt` stays
   *  the English reference the plan and the judge share); `answerTextEn` is the assessment model's
   *  English rendering of the original answer — a reading aid, never the record (evidence quotes
   *  the original script). Both absent on an English interview, an older payload, or a
   *  pre-migration API: nothing extra renders. */
  promptLocalized?: string | null;
  answerTextEn?: string | null;
}
/** Interview-level facts (0099: on the session — recorded once, never changed by a re-evaluation). */
interface ReportInterviewIntegrity {
  band: AuthenticityBand | null;
  authenticityScore: number | null;
  signals: IntegritySignal[];
  mediaSha256: string | null;
  checkedAt: string;
}
interface ReportSession {
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  humanRequested: boolean;
  /** WS4: the language the candidate chose at consent; absent/null = English. */
  language?: string | null;
}
interface ReportVerification {
  verified: boolean;
  method: string | null;
  simulated?: boolean;
  emailConfirmed?: boolean;
  score: number | null;
  createdAt: string;
}
/** Feature 1: browser-integrity signals surfaced in the report. These are ADVISORY proctoring
 *  flags for a human reviewer — never a score, never an automatic decision.
 *
 *  `IntegrityEventKind` is IMPORTED from the shared contract, never re-declared here. This file
 *  used to keep a hand-copy of the union, which is the same trick that let the emitter and the
 *  scorer drift apart and cost three signals; with the real type, the label maps below stop
 *  compiling the moment a kind is added. */
interface ReportIntegrity {
  counts: Partial<Record<IntegrityEventKind, number>>;
  events: { kind: IntegrityEventKind; at: string; detail: string | null }[];
}
/**
 * Feature 2: recording availability + a short-lived read SAS url when a blob exists.
 * Retention (0022): recordings are biometric data kept for a fixed window and then deleted by
 * the worker sweep. `expiresAt` is the reviewer's deadline; once the bytes are gone `available`
 * goes false and `purged` / `erased` say WHY. Every new field is optional so the report still
 * renders against an older API payload.
 */
interface ReportRecording {
  available: boolean;
  url?: string;
  expiresAt?: string;
  purged?: boolean;
  erased?: boolean;
  purgedAt?: string;
  sha256?: string;
  /** 0052: every live part, in order. Absent on an older payload → fall back to `url` as one part. */
  parts?: RecordingPart[];
  /** Sum of the parts' own durations, or null when any of them predates duration tracking. */
  coveredSeconds?: number | null;
  /** Wall clock of the session these parts belong to — what coverage is measured against. */
  sessionSeconds?: number | null;
  /** Parts already deleted while others survive (a mid-sweep, or a partial erasure). */
  partsDeleted?: number;
}

/** Whole days from now until `iso`. Negative once the instant has passed. */
function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/** Nudge the reviewer harder inside the last week of the window. */
function expiresSoon(iso: string): boolean {
  return daysUntil(iso) <= 7;
}

/** "12 days left" / "expires today" / "past its retention window" — never a bare number. */
function daysLeftLabel(iso: string): string {
  const d = daysUntil(iso);
  if (d < 0) return "past its retention window and awaiting deletion";
  if (d === 0) return "it expires today";
  if (d === 1) return "1 day left";
  return `${d} days left`;
}

/** Compact header summary of the same state, for the card's subtitle line. */
function recordingRetentionLabel(rec: ReportRecording): string {
  if (rec.purged || rec.erased) return "deleted";
  if (!rec.expiresAt) return "30-day retention";
  const d = daysUntil(rec.expiresAt);
  if (d < 0) return "awaiting deletion";
  if (d === 0) return "deleted today";
  return `${d} day${d === 1 ? "" : "s"} left`;
}
interface Report {
  /** The question-level INTERVIEW score (0105) and what this organization's lists rank by. Absent on an older API. */
  interview?: InterviewScoreData;
  rankingBasis?: RankingBasis;
  // resumeScore rides on the candidacy row (candidacies.resume_score, resumescore.ts) — the
  // resume-vs-job-description fit computed at application time, separate from the interview
  // score entirely. Not yet on the shared Candidacy type, so intersected here rather than
  // widening a type every other Candidacy consumer in the app would inherit.
  candidacy: Candidacy & { resumeScore?: number | null };
  candidateName: string | null;
  // Contact details from the invite (invites.email/phone/preferred_email) — null when the
  // candidacy predates invites carrying them, or was sourced without contact info.
  candidateEmail: string | null;
  candidatePhone: string | null;
  // The resume actually on file, if any. `url` is a short-lived read SAS — null when storage
  // isn't configured, never a broken link.
  resume: {
    filename: string;
    url: string | null;
    /** The org's Rescore switch is on, this org owns the candidate, nobody has decided, and the CV is scored. */
    canRescore?: boolean;
    /** The CV is on file with no score yet — offer the first-time match. */
    canScore?: boolean;
    scanId?: string;
    /** Details the scan read off the resume (0104); a field the resume doesn't state is absent. */
    profile?: ResumeProfileData | null;
    /** Keys a recruiter typed in (0106) rather than the scan reading them. */
    profileOverridden?: string[];
    /** Why the resume match is what it is — the scorer's stored breakdown (null on an unexplained legacy score). */
    match?: ResumeMatchView | null;
    /** Scores that earlier re-scores replaced, newest first. */
    previousScores?: { score: number | null; replacedAt: string; reason: string | null }[];
  } | null;
  // Wave 3: orgId + fulfilledByOrgId let the UI tell owner from fulfilling-vendor (decision is
  // owner-only; the server enforces 403, the UI just hides the buttons for the vendor).
  requisition: {
    title: string;
    /** The PINNED vertical and the EDITION of it this candidate was assessed against. Optional so
     *  an older report payload still renders — the key itself always rides along on `config`. */
    verticalKey?: string;
    verticalName?: string;
    verticalVersion?: number;
    config: WorkflowConfig;
    orgId?: string;
    engagementType?: EngagementType;
    fulfilledByOrgId?: string | null;
    /** Owner-controlled per-requisition flags (migration 0075) — absent on an older API payload,
     *  which degrades to every flag off (report.ts always sends this now, so absence is only ever
     *  a stale cache, never a real state). */
    flags?: {
      rescoreEnabled: boolean;
      customPromptEnabled: boolean;
      copyQuestionsEnabled: boolean;
    };
  };
  /** The requirement's CURRENT assessment criteria (0099). */
  assessment?: ReportAssessment | null;
  /** The current evaluation with full evidence; null = none succeeded yet. */
  evaluation: ReportEvaluation | null;
  evaluationHistory?: EvaluationSummary[];
  /** A re-evaluation that failed after the current one — it never replaced it. */
  lastFailedEvaluation?: EvaluationSummary | null;
  evaluationPending?: boolean;
  canReEvaluate?: boolean;
  interviewIntegrity?: ReportInterviewIntegrity | null;
  transcript: ReportTranscriptItem[];
  /** Shared report only: a redacted share withholds the name, contact, resume, recording and transcript. */
  redacted?: boolean;
  session: ReportSession | null;
  verification: ReportVerification | null;
  decisions: DecisionRecord[];
  notes: number;
  // Feature 1: advisory browser-integrity flags. Optional so the report still renders against an
  // older payload that predates the field (degrade to "clean session").
  integrity?: ReportIntegrity;
  // Feature 2: meeting recording — available + read SAS url when a stored blob exists.
  recording?: ReportRecording;
  // ── #43 strict/natural proctoring. ALL optional (`??` fallbacks below) so a payload from an
  // older API renders exactly as today. `interviewMode` also lives on config; interviewModeOf is
  // the one home of the "absent = natural" default.
  interviewMode?: InterviewMode;
  /** Speaking-time attention summary. null = natural mode or no gaze data at all; measured=false
   *  = strict mode but no gaze_watch_on proof, so nothing may be claimed (honest silence). */
  attention?: { measured: boolean; episodes: number; totalSeconds: number } | null;
  /** Pause → resume → bypass events, in order. Empty on a natural interview. */
  strictTimeline?: { kind: "strict_pause" | "strict_resume" | "strict_bypass"; at: string; detail: string | null }[];
  presenceWeightApplied?: number | null;
  /** How many of this interview's questions came from where — the transcript header's headline.
   *  null (or absent, on an older API) = a pre-wave interview with NO stored provenance: the
   *  header renders exactly as today, never a row of zeros pretending to be data. */
  questionProvenance?: {
    requirement: number;
    resume: number;
    standard: number;
    adaptive: number;
  } | null;
}
interface ReportResponse {
  report: Report;
}
interface NotesResponse {
  notes: CandidacyNote[];
}
interface ShareResponse {
  share: { url: string; redacted: boolean; expiresAt: string | null };
}
interface DecisionResponse {
  decision: DecisionRecord;
}

const BAND_META: Record<AuthenticityBand, { color: string; label: string; pill: string }> = {
  // "Live person confirmed", not "Verified real person": the band proves liveness + integrity
  // signals, not legal identity (product review 15, P0 wording).
  verified: { color: "var(--good)", label: "Live person confirmed", pill: "pill v" },
  needs_review: { color: "var(--warn)", label: "Needs review", pill: "pill r" },
  failed: { color: "var(--crit)", label: "Failed — held for you", pill: "pill f" },
};

const VERDICT_PILL: Record<IntegritySignal["verdict"], { cls: string; label: string }> = {
  pass: { cls: "pill v", label: "Pass" },
  flag: { cls: "pill r", label: "Flag" },
  fail: { cls: "pill f", label: "Fail" },
};

const SIGNAL_LABEL: Record<IntegritySignal["type"], string> = {
  identity_match: "Identity — 1:1 to ID document",
  liveness: "Liveness & injection detection",
  injection: "Virtual-camera / deepfake injection",
  singular_presence: "Singular presence — no second person",
  adaptive_challenge: "Adaptive challenge — work-bound",
  resume_claim: "Resume-claim verification",
};

// The kind→copy maps (order, phrases, timeline labels) live in integrityLabels.ts — typed over
// the full IntegrityEventKind union and unit-tested there, so a kind added to the shared enum
// can never render at a recruiter as a raw token.

/** Total seconds the candidate spent in one camera state, read off the `face_visible` receipts.
 *  An episode still open when the interview ended has no receipt and contributes nothing — we
 *  report the time we actually watched, never an estimate. */
function presenceSeconds(integrity: ReportIntegrity, from: "absent" | "multiple"): number {
  return (integrity.events ?? [])
    .filter((e) => e.kind === "face_visible")
    .reduce((sum, e) => {
      const parsed = parsePresenceReturn(e.detail);
      return sum + (parsed?.from === from ? parsed.seconds : 0);
    }, 0);
}

/** Build the advisory sentence list from the report's integrity block. Paste additionally sums
 *  the characters carried in each paste event's `detail` so the reviewer sees volume, not just a
 *  count (e.g. "Pasted 180 characters across 2 pastes"), and the two camera signals add the
 *  duration behind the count — "out of frame twice, 34s in total" is a fact a person can weigh,
 *  where "no_face ×2" could equally be two blinks or two coffee breaks. Returns [] when nothing
 *  was flagged. */
function integritySentences(integrity: ReportIntegrity | undefined): string[] {
  if (!integrity) return [];
  const counts = integrity.counts ?? {};
  const out: string[] = [];
  for (const kind of INTEGRITY_KIND_ORDER) {
    const n = counts[kind] ?? 0;
    if (n <= 0) continue;
    if (kind === "no_face" || kind === "second_face") {
      const secs = presenceSeconds(integrity, kind === "no_face" ? "absent" : "multiple");
      out.push(secs > 0 ? `${INTEGRITY_PHRASE[kind](n)} (${secs}s in total)` : INTEGRITY_PHRASE[kind](n));
    } else if (kind === "paste" || kind === "paste_blocked") {
      // Both paste kinds carry the character count in `detail` — volume is the fact a person can
      // weigh. The blocked variant PREVENTED the paste, and its sentence must keep saying so.
      const chars = (integrity.events ?? [])
        .filter((e) => e.kind === kind)
        .reduce((sum, e) => {
          const c = Number(e.detail);
          return sum + (Number.isFinite(c) && c > 0 ? c : 0);
        }, 0);
      if (kind === "paste") {
        out.push(
          chars > 0
            ? `Pasted ${chars} character${chars === 1 ? "" : "s"} across ${n} paste${n === 1 ? "" : "s"}`
            : INTEGRITY_PHRASE.paste(n),
        );
      } else {
        out.push(
          chars > 0
            ? `${INTEGRITY_PHRASE.paste_blocked(n)} (${chars} character${chars === 1 ? "" : "s"} attempted)`
            : INTEGRITY_PHRASE.paste_blocked(n),
        );
      }
    } else {
      out.push(INTEGRITY_PHRASE[kind](n));
    }
  }
  return out;
}

/**
 * "What could be seen", in a sentence — the honesty line that makes `presenceBasis` mean something.
 *
 * The presence figure is a baseline minus penalties, so on its own an 88 from a fully-watched
 * session and an 88 from a session where we only ever had tab focus are indistinguishable, and a
 * reviewer will read BOTH as "we watched them". This says which one it is, in words, every time:
 *
 *   • nothing reported at all → presence was NOT measured. No number, nothing deducted.
 *   • browser only            → a figure, plus an explicit statement that the camera was never
 *                               read, so nothing about being on or off camera is in it.
 *   • camera too              → a figure a reviewer may read as camera-backed.
 *
 * The camera line matters most for the candidate who declined the camera, or whose browser can't
 * detect faces: they must never be marked down for a detector that never ran, and the reviewer
 * must never be left to assume it did.
 */
function presenceLines(
  score: ReportEvaluation | null,
  weightApplied: number | null,
): { headline: string; detail: string } | null {
  if (!score) return null;
  const basis: Partial<PresenceBasis> = score.presenceBasis ?? { browser: false, camera: false };
  // #43: which split ACTUALLY went into the overall. `presenceWeightApplied` non-null names the
  // arithmetic in words (10% is the strict split, everything else prints its real percentage —
  // an unexpected weight is shown, never rounded to a familiar one); null keeps today's copy so
  // an older payload reads exactly as before. Naming the weight is invariant 6: without it a
  // strict 78 and a natural 78 look like the same number and scores stop being comparable.
  const weighting =
    weightApplied != null
      ? `Presence weighed ${Math.round(weightApplied * 100)}% of the overall${weightApplied === STRICT_PRESENCE_WEIGHT ? " (strict mode)" : ""
      }.`
      : score.presenceInScore
        ? "It contributes 5% of the overall score."
        : "It is observational only and does not move the score.";
  // basis.gaze is claimed ONLY on a real gaze_watch_on proof (`=== true` guards older payloads
  // where the key is absent) — the same honesty rule as the camera line.
  const gazeLine =
    basis.gaze === true
      ? " On-screen attention was also genuinely measured while the candidate was speaking — see the Attention note below."
      : "";
  if (score.presence == null) {
    return {
      headline: "Presence was not measured for this interview.",
      detail:
        "The candidate's browser reported no integrity signals at all, so there is no figure to show — and nothing has been deducted. Unknown is not the same as spotless, and it is not a mark against them either.",
    };
  }
  if (!basis.camera) {
    return {
      headline: `Presence ${Math.round(score.presence)} — from browser signals only.`,
      detail: `Camera presence was NOT measured on this device: either the candidate declined the camera or their browser has no face detector. Nothing about being on or off camera is in this figure. ${weighting}`,
    };
  }
  return {
    headline: `Presence ${Math.round(score.presence)} — browser signals and camera.`,
    detail: `A face detector genuinely ran on the candidate's device and produced real readings, so the camera signals below are evidence.${gazeLine} ${weighting}`,
  };
}

/**
 * "Why this question was asked", under a transcript row — the founder's ask made visible where
 * the customer reads it. A planned question gets its source chip and (when one was verified) the
 * exact phrase it was drawn from; an adaptive question gets the Bolt the transcript already uses
 * for live rows plus "Follow-up — <reason>". A row with no stored source renders NOTHING — most
 * stored interviews predate provenance, and silence is the only honest claim for those.
 */
function TranscriptProvenance({
  source,
  sourcePhrase,
}: {
  source?: string | null;
  sourcePhrase?: string | null;
}): ReactNode {
  if (source === "adaptive") {
    return (
      <div className="mut sm" style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
        <span style={{ display: "inline-flex", width: 13, color: "var(--violet)", flex: "none" }}>
          <Bolt />
        </span>
        <span>{adaptiveLabel(sourcePhrase)}</span>
      </div>
    );
  }
  const chip = sourceChip(source);
  if (!chip) return null;
  const quote = sourceQuote(source, sourcePhrase);
  return (
    <div style={{ marginBottom: 8 }}>
      <span className={chip.cls} style={{ fontSize: 12 }}>
        {chip.label}
      </span>
      {quote && (
        <div className="mut sm" style={{ marginTop: 4, fontStyle: "italic" }}>
          {quote}
        </div>
      )}
    </div>
  );
}

/**
 * The expanded body of one question in the Interview assistant: the question's provenance, the snippet pick,
 * the FULL answer (with the capture-loss / voice-edited notes and English rendering), then the evidence —
 * first why the QUESTION scored what it did, then the competency quotes this answer fed. The question line and
 * the play control live on the row itself; everything here is only shown once a reviewer opens the answer.
 */
function AnswerDetail({
  t,
  language,
}: {
  t: ReportTranscriptItem;
  language?: string | null;
}): ReactNode {
  const answer = t.verifiedText ?? t.answerText;
  return (
    <div className="t1-rv-q-detail">
      <div style={{ display: "grid", gap: 10 }}>
        <TranscriptProvenance source={t.source} sourcePhrase={t.sourcePhrase} />
        {t.options && t.options.length > 0 && (
          <div className="mut sm">
            {typeof t.pickedIndex === "number" ? (
              <>
                Picked {OPTION_LETTERS[t.pickedIndex] ?? t.pickedIndex + 1} — “{t.options[t.pickedIndex]}”
                {typeof t.correctIndex === "number" && (
                  <>
                    {" · "}
                    {t.pickCorrect === true ? (
                      <span className="pill t">best answer</span>
                    ) : (
                      <>
                        <span className="pill n">
                          best answer was {OPTION_LETTERS[t.correctIndex] ?? t.correctIndex + 1}
                        </span>{" "}
                        — “{t.options[t.correctIndex]}”
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>Shown {t.options.length} options — no pick was recorded.</>
            )}
          </div>
        )}
        {answer ? (
          <div>
            <div className="t1-rv-label" style={{ marginBottom: 5 }}>
              Answer
            </div>
            <p className="sm" style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{answer}</p>
            {/* 0063 — capture honesty. Only worth a line when the live capture materially
                under-heard; a 0-14% drift is normal ASR jitter. */}
            {t.verifiedText && (t.captureLossPct ?? 0) >= 15 && (
              <p className="mut sm" style={{ margin: "6px 0 0" }}>
                <Info /> Transcribed from the recording — the live capture missed ~{t.captureLossPct}% of
                this answer, so earlier views of it were incomplete.
              </p>
            )}
            {t.answeredBy === "voice_edited" && (
              <p className="mut sm" style={{ margin: "6px 0 0" }}>
                The candidate corrected the live captions of this answer before sending
                {t.verifiedText ? " (shown text is the recording's transcript)" : ""}.
              </p>
            )}
          </div>
        ) : (
          <p className="mut sm" style={{ margin: 0 }}>
            No text answer recorded.
          </p>
        )}
        {t.answerTextEn && language && language !== "en" && (
          <div
            className="mut sm"
            style={{ padding: "8px 10px", borderLeft: "3px solid var(--line)", background: "var(--surface-2)", borderRadius: 8 }}
          >
            <div style={{ fontWeight: 600, marginBottom: 3 }}>English rendering (by the assessment model)</div>
            <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{t.answerTextEn}</p>
          </div>
        )}
        {t.questionScore && (
          <div className="t1-rv-quality">
            <div className="t1-rv-label" style={{ marginBottom: 5 }}>
              Why this score
            </div>
            <span
              className={`pill ${t.questionScore.score == null ? "n" : t.questionScore.score >= 70 ? "v" : t.questionScore.score >= 40 ? "r" : "n"}`}
              title="This answer's own score, from the role and this question alone. It is not a competency score. Accent, tone and fluency are never scored."
            >
              Question score {t.questionScore.score ?? "—"}/100
            </span>
            {t.questionScore.reason && <span className="mut sm"> {t.questionScore.reason}</span>}
            {t.questionScore.span && <span className="mut sm"> — “{t.questionScore.span}”</span>}
            {!t.questionScore.counts && <span className="mut sm"> · not counted in the average</span>}
          </div>
        )}
        {/* What THIS answer evidenced (0100) — derived from the competency results, never a separate
            per-answer score: answers are evidence, competencies are what is assessed. */}
        {(t.evidence?.length ?? 0) > 0 && (
          <div>
            <div className="t1-rv-label" style={{ marginBottom: 5 }}>
              Competency evidence
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
              {t.evidence!.map((e, i) => (
                <li key={i} className="sm" style={{ lineHeight: 1.5 }}>
                  <span className="pill t" style={{ marginRight: 6 }}>
                    {e.label}
                  </span>
                  <span className="mut">{e.strength} evidence</span>
                  {e.span && <span className="mut"> — “{e.span}”</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {t.codeResult != null && <CodeRunSummary result={t.codeResult} />}
      </div>
    </div>
  );
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();


/** Strengths/concerns for the AI evaluation card, from the SAME competency results the breakdown
 *  shows. Only EVIDENCED competencies can be a strength or a concern — an insufficient one is a
 *  coverage gap, listed separately and never read as a weakness. */
function deriveEvaluation(competencies: ReportEvaluation["competencies"]): {
  strengths: { label: string; score: number; citedSpan: string }[];
  concerns: { label: string; score: number; citedSpan: string }[];
  notShown: string[];
} {
  const scored = competencies.filter((c) => c.status === "evidenced" && c.score != null) as (ReportEvaluation["competencies"][number] & { score: number })[];
  const row = (c: (typeof scored)[number]) => ({ label: c.label, score: c.score, citedSpan: c.evidence[0]?.span ?? "" });
  return {
    strengths: [...scored].filter((c) => c.score >= 70).sort((a, b) => b.score - a.score).slice(0, 3).map(row),
    concerns: [...scored].filter((c) => c.score < 55).sort((a, b) => a.score - b.score).slice(0, 3).map(row),
    notShown: competencies.filter((c) => c.status === "insufficient_evidence").map((c) => c.label),
  };
}

/** The top KPI strip's 4th tile — TalentHop's honest equivalent of a generic "Security" check:
 *  the SAME authenticity band and identity-check-ran fact `identityLine` already states in words,
 *  just as a glanceable tile. Never a new signal, never a new claim. */
function integrityTileMeta(
  checked: boolean,
  band: AuthenticityBand,
  idvRan: boolean,
): { label: string; detail: string; tone: "v" | "r" | "f" | "n" } {
  if (!checked) return { label: "—", detail: "Not checked yet", tone: "n" };
  if (band === "verified") return { label: "Clean", detail: "No integrity issues", tone: "v" };
  if (band === "failed") return { label: "Failed", detail: "Review the recording", tone: "f" };
  if (idvRan) return { label: "Flagged", detail: "Signals need review", tone: "r" };
  return { label: "Not checked", detail: "Identity check was off", tone: "n" };
}

/** SVG donut — a 0–100 score ringed in the caller's colour (the report's skill-composite hero). */
function ScoreRing({ score, color, size = 120 }: { score: number; color: string; size?: number }): ReactNode {
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 130 130" width={size} height={size} role="img" aria-label={`Score ${score} out of 100`}>
      <circle cx="65" cy="65" r={r} fill="none" stroke="var(--surface-2)" strokeWidth="11" />
      <circle
        cx="65"
        cy="65"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="11"
        strokeLinecap="round"
        strokeDasharray={`${(Math.max(0, Math.min(100, score)) / 100) * c} ${c}`}
        transform="rotate(-90 65 65)"
      />
      <text x="65" y="72" textAnchor="middle" fontSize="30" fontWeight="800" fill="var(--ink)">
        {score}
      </text>
    </svg>
  );
}

/** Local modal — fixed overlay, backdrop click closes (no shared modal class exists yet). */
function Modal({ title, onClose, children, maxWidth = 540 }: { title: string; onClose: () => void; children: ReactNode; maxWidth?: number }): ReactNode {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,14,20,.5)",
        backdropFilter: "blur(2px)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
        padding: 20,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth, maxHeight: "calc(100vh - 40px)", overflowY: "auto", boxShadow: "var(--shadow)" }}
      >
        <div className="card-h">
          <h3>{title}</h3>
          <button className="btn sm ghost" onClick={onClose} aria-label="Close" data-tip="Close" data-tip-pos="left">
            ✕
          </button>
        </div>
        <div className="card-b">{children}</div>
      </div>
    </div>
  );
}

/**
 * Recording playback.
 *
 * Two things were broken here and they compound each other.
 *
 * 1. A MediaRecorder WebM has NO duration in its header, so `video.duration` reads Infinity and
 *    the browser's own scrubber has nothing to scale against: the clock counts up while the bar
 *    sits at zero, and a reviewer can't tell where they are or how much is left. Seeking far past
 *    the end forces the browser to demux to the last cluster and compute the real duration; we
 *    then put the playhead back. Where even that fails we fall back to the duration the recorder
 *    measured and registered, and we draw the bar ourselves off whichever number is real.
 *
 * 2. A session is several PARTS (the candidate's browser rotates its recorder so a closed tab
 *    can't take the whole interview with it, and a resumed session adds more). They play back to
 *    back here, so the reviewer sees one continuous review, but the header says plainly how many
 *    parts there are and how much time they add up to — the parts are contiguous only in the
 *    order they were recorded, not necessarily in wall-clock time.
 */
/** A small icon that shows its detail on hover or keyboard focus — for notices that should be one glance away, not a
 *  permanent box. `tone="warn"` keeps the icon amber so a real caveat is still noticed. */
function InfoTip({ children, label, tone }: { children: ReactNode; label: string; tone?: "warn" }): ReactNode {
  return (
    <span className={`t1-tip${tone === "warn" ? " warn" : ""}`} tabIndex={0} role="note" aria-label={label}>
      <span className="t1-tip-i" aria-hidden="true">
        {tone === "warn" ? <Alert /> : <Info />}
      </span>
      <span className="t1-tip-b" role="tooltip">
        {children}
      </span>
    </span>
  );
}

/** What a parent can ask of the player: jump to a moment (seconds from the start of the whole recording) and play. */
export interface RecordingControl {
  seekTo: (globalSeconds: number) => void;
}

function RecordingPlayer({
  parts,
  controlRef,
  onTime,
}: {
  parts: RecordingPart[];
  controlRef?: MutableRefObject<RecordingControl | null>;
  /** Called as the recording plays, with seconds from the start of the WHOLE recording (all parts). */
  onTime?: (globalSeconds: number) => void;
}): ReactNode {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [index, setIndex] = useState(0);
  /** A seek that has to wait for another part (or a not-yet-loaded file) to be ready. */
  const pendingSeekRef = useRef<number | null>(null);
  const indexRef = useRef(0);
  indexRef.current = index;
  const partsRef = useRef(parts);
  partsRef.current = parts;
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;
  const [position, setPosition] = useState(0);
  /** The duration the ELEMENT eventually admitted to, once recovered. Null while non-finite. */
  const [elementDuration, setElementDuration] = useState<number | null>(null);
  const recoveringRef = useRef(false);
  // One restore per part: the seek-back must never fight a reviewer who is already scrubbing.
  const restoredRef = useRef(false);

  const part = parts[index];
  const registered = part?.durationSeconds ?? null;
  const duration = elementDuration ?? registered;

  // A new part is a new file: nothing about the last one's duration or position applies.
  useEffect(() => {
    setElementDuration(null);
    setPosition(0);
    recoveringRef.current = false;
    restoredRef.current = false; // each part recovers (and restores) once, on its own
  }, [index]);

  const applyPendingSeek = useCallback((): void => {
    const v = videoRef.current;
    const at = pendingSeekRef.current;
    if (!v || at == null) return;
    pendingSeekRef.current = null;
    try {
      v.currentTime = at;
      void v.play().catch(() => {});
    } catch {
      /* a browser that refuses the seek leaves the reviewer on the native scrubber */
    }
  }, []);

  useEffect(() => {
    if (!controlRef) return;
    controlRef.current = {
      seekTo: (global: number): void => {
        const ps = partsRef.current;
        // Walk the parts by their recorded lengths; a part with no recorded length can only be the last one we can reach.
        let rest = Math.max(0, global);
        let target = 0;
        for (let i = 0; i < ps.length; i++) {
          const len = ps[i]?.durationSeconds;
          if (typeof len !== "number" || rest < len || i === ps.length - 1) {
            target = i;
            break;
          }
          rest -= len;
        }
        pendingSeekRef.current = rest;
        if (target !== indexRef.current) setIndex(target);
        else applyPendingSeek();
      },
    };
    return () => {
      controlRef.current = null;
    };
  }, [controlRef, applyPendingSeek]);

  const readDuration = useCallback((): void => {
    const v = videoRef.current;
    if (!v) return;
    if (Number.isFinite(v.duration) && v.duration > 0) {
      setElementDuration(v.duration);
      // Recovery ends HERE, where the real duration arrives — and the playhead must come back in
      // the same breath. Clearing the flag and leaving the restore to `timeupdate` was a race the
      // browser usually won: Chrome fires `durationchange` first, so the flag was already false by
      // the time `timeupdate` ran and the reviewer opened a recording parked at its own end
      // (bar at 100%, Play firing `ended` instantly and skipping straight to part 2).
      if (recoveringRef.current) {
        recoveringRef.current = false;
        if (!restoredRef.current) {
          restoredRef.current = true;
          try {
            v.currentTime = 0;
          } catch {
            /* a browser that refuses the seek back leaves the native scrubber to it */
          }
        }
      }
      applyPendingSeek();
      return;
    }
    if (recoveringRef.current) return;
    recoveringRef.current = true;
    // The seek-to-end trick. Safe because it only ever runs on a file whose duration the browser
    // has already told us it doesn't know, and only before the reviewer has started watching.
    try {
      v.currentTime = 1e101;
    } catch {
      recoveringRef.current = false; // seeking refused — the registered duration is what we have
    }
  }, [applyPendingSeek]);

  const onTimeUpdate = useCallback((): void => {
    const v = videoRef.current;
    if (!v) return;
    // Fallback for a browser that recovers the duration without firing `durationchange`. The
    // `restoredRef` latch is what keeps it from yanking a reviewer who has already started
    // scrubbing — whichever path runs first wins, and the second is a no-op.
    if (recoveringRef.current && Number.isFinite(v.duration) && v.duration > 0) {
      recoveringRef.current = false;
      setElementDuration(v.duration);
      if (!restoredRef.current) {
        restoredRef.current = true;
        try {
          v.currentTime = 0;
        } catch {
          /* nothing to restore to */
        }
      }
      applyPendingSeek();
      return;
    }
    setPosition(v.currentTime);
    const ps = partsRef.current;
    const before = ps.slice(0, indexRef.current).reduce((n, p) => n + (p.durationSeconds ?? 0), 0);
    onTimeRef.current?.(before + v.currentTime);
  }, [applyPendingSeek]);

  const onEnded = useCallback((): void => {
    setIndex((i) => {
      if (i + 1 >= parts.length) return i;
      // Roll straight into the next part so a multi-part recording watches like one recording.
      window.setTimeout(() => void videoRef.current?.play().catch(() => { }), 0);
      return i + 1;
    });
  }, [parts.length]);

  if (!part?.url) return null;

  const before = parts.slice(0, index).reduce((n, p) => n + (p.durationSeconds ?? 0), 0);
  const everyPartTimed = parts.every((p) => typeof p.durationSeconds === "number");
  const totalSeconds = everyPartTimed ? parts.reduce((n, p) => n + (p.durationSeconds ?? 0), 0) : null;
  const pct = duration && duration > 0 ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <>
      <video
        key={part.url}
        ref={videoRef}
        controls
        src={part.url}
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        style={{ width: "100%", maxHeight: 440, borderRadius: 8, background: "#000" }}
      />

      {/* Our own bar. The native one is repaired by the recovery above where the browser allows
          it, but this one is driven by a number we can always account for, so the reviewer is
          never left watching a clock climb against a bar that doesn't move. */}
      <div className="meter" style={{ marginTop: 8 }}>
        <div className="bar">
          <i style={{ width: `${pct}%` }} />
        </div>
        <span className="val tnum" style={{ width: "auto" }}>
          {formatClock(position)} / {duration ? formatClock(duration) : "—:—"}
        </span>
      </div>
      <div className="mut sm" style={{ marginTop: 4 }}>
        {parts.length > 1 && (
          <>
            Part {index + 1} of {parts.length}
            {totalSeconds !== null && <> · {formatClock(before + position)} of {formatClock(totalSeconds)} overall</>}
            {" · "}
          </>
        )}
        {duration === null && "This recording's length isn't stored and the browser won't report it, so the bar can't be scaled — play it through to see all of it."}
        {duration !== null && elementDuration === null && "Length taken from the recorder's own measurement — this file carries none of its own."}
      </div>

      {parts.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {parts.map((p, i) => (
            <button
              key={p.url ?? i}
              type="button"
              className={`btn sm ${i === index ? "" : "ghost"}`}
              onClick={() => setIndex(i)}
            >
              Part {i + 1}
              {typeof p.durationSeconds === "number" && ` · ${formatClock(p.durationSeconds)}`}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Users · candidate review (GET /candidacies/{id}/report): authenticity ring, integrity grid,
 *  cited competency bars, transcript, notes, share link, and the human-gated decision. */
/**
 * The Resume / JD match card: the CV's own score (never blended into the interview), its file, the
 * scores earlier re-scores replaced, and the first-score / re-score controls. Rendered whether or not
 * the interview has been evaluated yet — a CV is scored on its own clock.
 */
function ResumeMatchCard({
  report,
  isFulfillingVendor,
  onChanged,
}: {
  report: Report;
  isFulfillingVendor: boolean;
  onChanged: () => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  const { readOnly } = useReportSource();
  // The owning org's editors only: a fulfilling vendor sees the details, not the pen — nor does a shared report.
  const canEdit = !readOnly && !isFulfillingVendor && !!report.resume;
  return (
    <>
      {(report.candidacy.resumeScore != null || report.resume) && (
        <div className="card t1-rv-kpi t1-rv-kpi-resume">
          <div className="t1-rv-kpi-l">Resume match</div>
          <div className="t1-rv-kpi-n tnum" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {report.candidacy.resumeScore != null ? `${Math.round(report.candidacy.resumeScore)}%` : "—"}
            {report.candidacy.resumeScore != null && report.resume && (
              <button
                type="button"
                className="t1-icon-btn t1-rme-btn"
                onClick={() => setExplainOpen(true)}
                aria-label="Why this resume match score"
                data-tip="Why this score"
              >
                <Eye />
              </button>
            )}
          </div>
          {report.candidacy.resumeScore != null && (
            <div className="t1-bt neutral">
              <i style={{ width: `${report.candidacy.resumeScore}%` }} />
            </div>
          )}
          <div className="t1-rv-kpi-d">
            {report.resume &&
              (report.resume.url ? (
                <a href={report.resume.url} target="_blank" rel="noreferrer" title={report.resume.filename}>
                  View resume
                </a>
              ) : (
                <span title={report.resume.filename}>Resume on file — viewing unavailable right now.</span>
              ))}
          </div>
          {editing ? (
            <ResumeProfileEditor
              candidacyId={report.candidacy.id}
              profile={report.resume?.profile ?? {}}
              overridden={(report.resume?.profileOverridden ?? []) as ResumeOverrideKey[]}
              onCancel={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                onChanged();
              }}
            />
          ) : (
            <>
              {hasResumeProfile(report.resume?.profile) && (
                <ResumeProfileLines profile={report.resume!.profile!} edited={report.resume?.profileOverridden} />
              )}
              {canEdit && (
                <div className="t1-rv-profile-head">
                  <button type="button" className="btn ghost sm" onClick={() => setEditing(true)}>
                    {hasResumeProfile(report.resume?.profile) ? "Edit details" : "Add details"}
                  </button>
                </div>
              )}
              {(canEdit || (readOnly && !report.redacted)) && (
                <CandidateDetailsFields candidacyId={report.candidacy.id} reqId={report.candidacy.requisitionId} />
              )}
            </>
          )}
          {explainOpen && (
            <Modal title="Why this resume match score" onClose={() => setExplainOpen(false)} maxWidth={760}>
              <ResumeMatchExplain score={report.candidacy.resumeScore ?? null} match={report.resume?.match} />
            </Modal>
          )}
          {report.resume?.previousScores && report.resume.previousScores.length > 0 && (
            <div className="mut sm" style={{ marginTop: 6 }}>
              Earlier:{" "}
              {report.resume.previousScores
                .map((h) => `${h.score ?? "—"}% (${new Date(h.replacedAt).toLocaleDateString()})`)
                .join(" · ")}
            </div>
          )}
          {!readOnly && report.resume?.canScore && report.resume.scanId && !isFulfillingVendor && (
            <ResumeScoreControl
              requisitionId={report.candidacy.requisitionId}
              scanId={report.resume.scanId}
              onDone={onChanged}
            />
          )}
          {!readOnly && report.resume?.canRescore && !isFulfillingVendor && (
            <ResumeRescoreControl candidacyId={report.candidacy.id} onDone={onChanged} />
          )}
        </div>
      )}
    </>
  );
}

/** Not invited yet — Send interview applies (mirrors the server's INVITABLE_STATES in utils/cvScreen.ts). */
const INVITABLE_STATES = new Set(["sourced", "screened", "shortlisted"]);
/** Invited and not finished — a fresh copy of the interview link can be shared by hand. */
const LINK_STATES = new Set(["invited", "self_scheduled", "reminded", "interviewing"]);

export function Review(): ReactNode {
  const { candidacyId = "" } = useParams();
  // Shared report (reportSource.tsx): the same page, read from the share, with every action hidden.
  const { shareToken, readOnly } = useReportSource();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectConfirmed, setRejectConfirmed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareRedacted, setShareRedacted] = useState(true);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // One shared toast for both copy actions below — bottom-right, auto-dismissing. `tone: "warn"`
  // for "there was nothing to copy" (not really a failure, just an honest empty state), "good" for
  // an actual successful copy.
  const [toast, setToast] = useState<{ tone: "good" | "warn"; text: string } | null>(null);
  const showToast = (tone: "good" | "warn", text: string): void => {
    setToast({ tone, text });
    window.setTimeout(() => setToast((t) => (t?.text === text ? null : t)), 2600);
  };
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  // Header actions: the activity log (same log as the Candidates list panel), Send interview for
  // someone not yet invited, and a fresh interview link to share by hand when the email didn't land.
  const [logOpen, setLogOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [interviewLink, setInterviewLink] = useState<{ url: string; emailedTo: string | null; fresh: boolean } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [showAllTimeline, setShowAllTimeline] = useState(false);
  // The recording and the Interview assistant talk through these: a question seeks the video, the playhead picks the question.
  const recordingControl = useRef<RecordingControl | null>(null);
  const [playhead, setPlayhead] = useState<number | null>(null);
  // Evidence stays available on request rather than dominating the page — one shared popup for
  // every citedSpan quote, whichever competency card/list it was opened from.

  const reportQuery = useQuery({
    queryKey: shareToken ? ["sharedReport", shareToken] : ["report", candidacyId],
    queryFn: () =>
      api.get<ReportResponse>(shareToken ? `/shared/${shareToken}` : `/candidacies/${candidacyId}/report`),
    enabled: readOnly || candidacyId.length > 0,
  });
  // Provenance: was this interview built from the candidate's own resume, or the shared plan?
  const sendInterview = useMutation({
    mutationFn: (reqId: string) =>
      api.post<{
        invited: { candidacyId: string; email: string | null; emailed: boolean; inviteUrl: string }[];
        skipped: { candidacyId: string; reason: string }[];
      }>(`/requisitions/${reqId}/candidates/invite`, { candidacyIds: [candidacyId] }),
    onSuccess: (res) => {
      const sent = res.invited[0];
      void queryClient.invalidateQueries({ queryKey: ["report", candidacyId] });
      void queryClient.invalidateQueries({ queryKey: ["org-candidates"] });
      setSendConfirmOpen(false);
      if (sent) {
        setLinkCopied(false);
        setInterviewLink({ url: sent.inviteUrl, emailedTo: sent.emailed ? sent.email : null, fresh: false });
      } else {
        showToast("warn", "Already invited — use Copy interview link instead.");
      }
    },
  });
  const reissueLink = useMutation({
    mutationFn: () => api.post<{ inviteUrl: string; expiresAt: string }>(`/candidacies/${candidacyId}/interview-link`),
    onSuccess: (res) => {
      setLinkCopied(false);
      setInterviewLink({ url: res.inviteUrl, emailedTo: null, fresh: true });
    },
    onError: (e) => showToast("warn", e instanceof ApiError ? e.message : "Couldn't get the interview link."),
  });
  async function copyInterviewLink(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
    } catch {
      showToast("warn", "Couldn't copy automatically — select the link and copy it.");
    }
  }

  const questionsQuery = useQuery({
    queryKey: ["candidacy-questions", candidacyId],
    queryFn: () =>
      api.get<{
        source: "candidacy" | "requisition" | "bank";
        planSource: "resume_tailored" | "requisition" | "manual" | null;
        generatedAt: string | null;
      }>(`/candidacies/${candidacyId}/questions`),
    enabled: !readOnly && candidacyId.length > 0,
    staleTime: 60_000,
  });
  const notesQuery = useQuery({
    queryKey: ["notes", candidacyId],
    queryFn: () => api.get<NotesResponse>(`/candidacies/${candidacyId}/notes`),
    enabled: !readOnly && candidacyId.length > 0,
  });

  const decide = useMutation({
    mutationFn: (input: { action: "advance" | "reject"; reason?: string }) =>
      api.post<DecisionResponse>(`/candidacies/${candidacyId}/decision`, input),
    onSuccess: () => {
      setRejectOpen(false);
      setRejectReason("");
      setRejectConfirmed(false);
      void queryClient.invalidateQueries({ queryKey: ["funnel"] });
    },
    // Refetch the report on FAILURE too, not only on success. A decision can commit server-side
    // and still land here as an error (the response lost after the write, or a colleague decided
    // first); leaving the panel on its pre-attempt data is what made a recorded rejection look
    // un-recorded and sent the next Advance click into an unexplained 409.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["report", candidacyId] });
    },
  });

  // Recruiter-editable organizational tag (migration 0077) — fires immediately on select, separate
  // from the Decision panel's Advance/Reject (audited, notifies the candidate). See
  // PIPELINE_STATUSES' doc comment (packages/shared/src/types.ts) for why the two never conflate.
  const updateStatus = useMutation({
    mutationFn: (pipelineStatus: PipelineStatus | null) =>
      api.patch(`/requisitions/${report?.candidacy.requisitionId}/candidates/${candidacyId}`, {
        pipelineStatus,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report", candidacyId] });
    },
  });

  const addNote = useMutation({
    mutationFn: () => api.post(`/candidacies/${candidacyId}/notes`, { body: noteBody.trim() }),
    onSuccess: () => {
      setNoteBody("");
      void queryClient.invalidateQueries({ queryKey: ["notes", candidacyId] });
    },
  });

  const share = useMutation({
    mutationFn: () =>
      api.post<ShareResponse>(`/candidacies/${candidacyId}/share`, {
        redacted: shareRedacted,
        recipientEmail: shareEmail.trim() || undefined,
      }),
    onSuccess: (res) => {
      const url = res.share.url.startsWith("http")
        ? res.share.url
        : `${window.location.origin}${res.share.url}`;
      setShareUrl(url);
    },
  });

  const report = reportQuery.data?.report;
  const notes = notesQuery.data?.notes ?? [];
  // THE EVALUATION (0099) and the interview's own integrity facts — two separate things now: a
  // re-evaluation changes the first and never the second.
  const ev = report?.evaluation ?? null;
  const integ = report?.interviewIntegrity ?? null;
  const band: AuthenticityBand = integ?.band ?? "needs_review";
  // The HERO number is capability (presence folded in only when the Owner switch is on). Null when
  // nothing had enough evidence to judge — shown as "—", never as 0.
  const composite: number | null = ev ? (ev.overall ?? ev.capability) : null;
  // Identity truth for THIS interview: no integrity signals === the identity/liveness check never
  // ran (IDV off / no vendor wired). Say that plainly — never imply the candidate FAILED identity.
  const idvRan = (integ?.signals.length ?? 0) > 0;
  const identityLine = !integ
    ? ""
    : band === "verified"
      ? "Identity verified — real, present, singular human, recomputed server-side."
      : band === "failed"
        ? "Integrity check failed — held for your review. Watch the recording before you decide."
        : idvRan
          ? "Identity signals flagged — review the recording before ranking."
          : "Identity check was off for this interview, so no identity claim is made — the score reflects answer content only.";
  const name = report?.candidateName ?? "Candidate";
  // Advisory integrity flags — a factual sentence per non-zero signal (empty === clean session).
  const flagSentences = integritySentences(report?.integrity);
  // #43: the proctoring preset this interview RAN under. The top-level field when the API sends
  // it, else derived from the config that rode along all along — interviewModeOf owns the
  // "absent = natural" default, so a pre-#43 report renders as the natural interview it was.
  const interviewMode: InterviewMode =
    report?.interviewMode ?? interviewModeOf(report?.requisition.config);
  // Defensive read from either home (score block or report top level) — null = switch off.
  const presenceWeightApplied = report?.presenceWeightApplied ?? null;
  // …and the basis behind the presence figure, said out loud rather than left to be assumed.
  const presence = presenceLines(ev, presenceWeightApplied);
  // Attention (#43): undefined = older API (say nothing), null = natural mode / no gaze data.
  const attention = report?.attention ?? null;
  const strictTimeline = report?.strictTimeline ?? [];
  // The decision the SERVER holds, read off candidacy.state — the same field the endpoint's
  // conflict guard reads. Deriving it from the decision records instead let the panel disagree
  // with the API about whether this candidate was still open to a decision.
  const settled = settledDecision(report?.candidacy.state, report?.decisions);
  // The hero verdict and the strengths/concerns, from the SAME competency results the breakdown shows.
  // Coverage-aware (0100): a high capability on a small share of the criteria is "Limited evidence".
  const match = ev ? matchMetaOf(ev.capability, ev.coverage) : null;
  const verdict = ev ? verdictOf(ev.capability, ev.coverage, band, ev.breakdown) : null;
  const evaluation = deriveEvaluation(ev?.competencies ?? []);
  const integrityTile = integrityTileMeta(!!integ, band, idvRan);
  // The recording's parts and how much of the session they cover — computed once so the card header can carry
  // the coverage and retention notices as hover icons instead of always-visible boxes.
  const recCover = (() => {
    const rec = report?.recording;
    if (!report || !rec?.available || !rec.url) return null;
    // An older payload carries one url and no parts; treat it as a single part so this renders as it always did.
    const parts: RecordingPart[] = rec.parts && rec.parts.length > 0 ? rec.parts : [{ url: rec.url, expiresAt: rec.expiresAt }];
    return recordingCoverage(
      parts,
      rec.coveredSeconds ?? null,
      // The API measures the session that OWNS these parts; the report's own session is the fallback.
      rec.sessionSeconds ?? spanSeconds(report.session?.startedAt, report.session?.completedAt),
    );
  })();


  // Wave 3: on a vendor-fulfilled requisition, only the OWNER org decides. If I'm the fulfilling
  // vendor (I can see it via fulfilled_by, but don't own it), hide the decision controls — the
  // server returns 403 regardless, so this just reflects the rule in the UI.
  const reqInfo = report?.requisition;
  const myOrgId = me?.orgId ?? null;
  const isFulfillingVendor = !!(
    reqInfo?.fulfilledByOrgId &&
    myOrgId &&
    reqInfo.fulfilledByOrgId === myOrgId &&
    reqInfo.orgId != null &&
    reqInfo.orgId !== myOrgId
  );
  const engagement: EngagementType = reqInfo?.engagementType ?? reqInfo?.config.engagementType ?? "fte";

  // One mutation serves both buttons, so the copy has to be keyed on which action failed.
  const decideError = decisionErrorMessage(decide.error, decide.variables?.action);
  // Abandoning an attempt drops its error with it — the same banner renders in the panel behind
  // the modal, so a cancelled failed reject used to leave a red "retry in a moment" sitting on a
  // candidate nobody was deciding on any more.
  const closeReject = (): void => {
    decide.reset();
    setRejectOpen(false);
  };

  const copyShare = (url: string): void => {
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };

  const copyTranscript = (items: readonly ReportTranscriptItem[]): void => {
    const text = items
      .map((t) => `Q${t.seq}: ${t.prompt}\n\nA: ${t.verifiedText ?? t.answerText ?? "No text answer recorded."}`)
      .join("\n\n---\n\n");
    void navigator.clipboard?.writeText(text).then(() => {
      showToast("good", "Copied the questions and answers.");
    });
  };

  // Copies the EXACT request sent to the judge (system prompt + transcript/competency/answer
  // payload) for this candidate's latest scoring pass — GET /candidacies/{id}/judge-call, gated
  // (server-side, re-checked there) the same as the custom judge-prompt editor: hiring_manager/
  // super_admin only, only when the org's customPromptEnabled flag is on. Fetched on click, not
  // eagerly, since this is more sensitive than the plain Q&A copy above it. `request: null` means
  // no real judge call was ever logged (a simulated interview, or one scored before the raw
  // capture shipped) — an honest empty state, not an error.
  const copyJudgePrompt = useMutation({
    mutationFn: () => api.get<{ request: unknown; createdAt: string | null }>(`/candidacies/${candidacyId}/judge-call`),
    onSuccess: (res) => {
      if (!res.request) {
        showToast("warn", "Nothing captured for this interview — it was likely scored by the simulator, not a real AI call.");
        return;
      }
      void navigator.clipboard?.writeText(JSON.stringify(res.request, null, 2)).then(() => {
        showToast("good", "Copied the exact prompt sent to the judge.");
      });
    },
  });

  // Opened from the Interviews tab → go back there (with its requirement filter); otherwise the
  // requisition's funnel, as before.
  const backTo = (location.state as { backTo?: unknown } | null)?.backTo;
  const backToInterviews =
    typeof backTo === "string" && backTo.startsWith("/app/interviews") ? backTo : undefined;

  return (
    <section className="t1-rv">
      <div className="page-h t1-rv-head">
        <div>
          {!readOnly && <Link
            className="btn sm ghost"
            to={
              backToInterviews ??
              (report ? `/app/requisitions/${report.candidacy.requisitionId}` : "/app/requisitions")
            }
            style={{ marginBottom: 8 }}
          >
            <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
              <Arrow />
            </span>{" "}
            {backToInterviews ? "Back to interviews" : "Back to funnel"}
          </Link>}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="t1-rv-av" aria-hidden="true">
              {initials(name)}
            </span>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <h1 style={{ margin: 0 }} className="t1-rv-name" title={name}>{name}</h1>
                {(report?.candidateEmail || report?.candidatePhone) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    {report?.candidateEmail && (
                      <a
                        href={`mailto:${report.candidateEmail}`}
                        className="mut sm"
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "none" }}
                      >
                        <span style={{ display: "inline-flex", width: 13, flex: "none" }}>
                          <Mail />
                        </span>
                        {report.candidateEmail}
                      </a>
                    )}
                    {report?.candidatePhone && (
                      <a
                        href={`tel:${report.candidatePhone}`}
                        className="mut sm"
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "none" }}
                      >
                        <span style={{ display: "inline-flex", width: 13, flex: "none" }}>
                          <Phone />
                        </span>
                        {report.candidatePhone}
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="sub">
                {report
                  ? `${report.requisition.title} · ${report.requisition.verticalName ?? prettyId(report.requisition.config.verticalKey)
                  }${report.requisition.verticalVersion ? ` v${report.requisition.verticalVersion}` : ""} · ${report.session?.completedAt
                    ? `interviewed ${new Date(report.session.completedAt).toLocaleString()}`
                    : "interview in progress"
                  }`
                  : "Loading report…"}
              </div>
              <LanguagePill language={report?.session?.language} />
              {report && !readOnly && (
                <div style={{ position: "relative", display: "inline-flex", alignItems: "center", marginTop: 6 }}>
                  <select
                    className={pipelineStatusPillClass(report.candidacy.pipelineStatus)}
                    style={{
                      appearance: "none",
                      WebkitAppearance: "none",
                      cursor: "pointer",
                      width: "auto",
                      display: "inline-block",
                      border: !report.candidacy.pipelineStatus ? "1px solid var(--line)" : "1px solid transparent",
                      background: !report.candidacy.pipelineStatus ? "var(--surface)" : undefined,
                      color: !report.candidacy.pipelineStatus ? "var(--ink)" : undefined,
                      padding: "4px 26px 4px 10px",
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 16,
                      boxShadow: !report.candidacy.pipelineStatus ? "var(--shadow-sm)" : undefined,
                      outline: "none",
                    }}
                    value={report.candidacy.pipelineStatus ?? ""}
                    disabled={updateStatus.isPending}
                    onChange={(e) => updateStatus.mutate((e.target.value || null) as PipelineStatus | null)}
                    title="Your own tracking tag — separate from the Hiring Decision, never affects scoring or the hiring decision"
                  >
                    <option value="">Untagged</option>
                    {PIPELINE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {PIPELINE_STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                  <span
                    style={{
                      position: "absolute",
                      right: 9,
                      pointerEvents: "none",
                      fontSize: 8,
                      color: !report.candidacy.pipelineStatus ? "var(--ink-mute)" : "currentColor",
                      opacity: 0.7,
                    }}
                  >
                    ▼
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {report && !readOnly && !isFulfillingVendor && (
            <CvAddReplace
              candidacyId={report.candidacy.id}
              requisitionId={report.candidacy.requisitionId}
              scanId={report.resume?.scanId ?? null}
              hasCv={!!report.resume}
              onDone={() => void reportQuery.refetch()}
            />
          )}
          {report && !readOnly && (
            <button className="btn" type="button" onClick={() => setLogOpen(true)} title="Everything that happened to this candidate on this requirement">
              <ScrollText size={14} aria-hidden="true" /> Activity log
            </button>
          )}
          {report && !readOnly && !report.requisition.config.sourcing && INVITABLE_STATES.has(report.candidacy.state) && (
            <button
              className="btn"
              type="button"
              disabled={sendInterview.isPending}
              onClick={() => { sendInterview.reset(); setSendConfirmOpen(true); }}
              title="Email this candidate their voice AI interview link"
            >
              <Send /> Send interview
            </button>
          )}
          {report && !readOnly && LINK_STATES.has(report.candidacy.state) && !report.session?.completedAt && (
            <button
              className="btn"
              type="button"
              disabled={reissueLink.isPending}
              onClick={() => reissueLink.mutate()}
              title="Get the interview link to share by hand (WhatsApp, SMS…) — the earlier link stops working"
            >
              <Copy /> {reissueLink.isPending ? "Getting link…" : "Copy interview link"}
            </button>
          )}
          {!readOnly && (
            <button className="btn" onClick={() => { setShareOpen(true); setShareUrl(null); }} disabled={!report}>
              <Copy /> Share results
            </button>
          )}
          {reqInfo && engagement === "staff_aug" && (
            <span className={engagementPillClass(engagement)}>
              {engagementLabel(engagement, reqInfo.config)}
            </span>
          )}
          {questionsQuery.data &&
            (() => {
              const { source, planSource, generatedAt } = questionsQuery.data;
              // Honesty: only vouch for a tailored/edited plan the COMPLETED interview actually used.
              // A candidacy plan created or edited AFTER the interview started (a late async landing,
              // or a post-hoc regenerate/edit) doesn't describe what was asked — so don't badge it as
              // tailored. "Standard plan" (no candidacy plan) is always accurate: none can be deleted,
              // so its absence now means none existed at interview time either.
              const startedAt = report?.session?.startedAt;
              const planPredatesInterview =
                !generatedAt || (!!startedAt && new Date(generatedAt) <= new Date(startedAt));
              if (source === "candidacy" && !planPredatesInterview) return null;
              const b = planBadge(source, planSource);
              return (
                <span className={b.cls} title="How this candidate's interview questions were authored">
                  {b.label}
                </span>
              );
            })()}
          {/* #43: strict is the exception, so only strict gets a badge — natural is the default
              and badging it would imply a third state exists. */}
          {interviewMode === "strict" && (
            <span className="pill pv" title="This interview ran under the strict proctoring preset: camera + fullscreen required, paste disabled, speaking-time attention noted.">
              Strict mode
            </span>
          )}
          {integ && <span className={BAND_META[band].pill}>{BAND_META[band].label}</span>}
        </div>
      </div>

      {reportQuery.isLoading && <div className="card pad mut sm">Loading the verified report…</div>}

      {reportQuery.isError && (
        <div className="banner crit">
          <span>Couldn't load this report. It may not exist or you may not have access.</span>
        </div>
      )}

      {!reportQuery.isLoading && !reportQuery.isError && report && (
        <div className="t1-rv-body">
          {/* ══ 1. HIRING DECISION — a sticky bar (audit 15 / top-15 #12) so the human decision is
                always in reach while reading the evidence. Deliberately separate from the AI
                evaluation below: the engine's read is one input, the recruiter's call is the
                decision. */}
          <div className="t1-rv-decide" role="region" aria-label="Hiring decision">
            <div className="t1-rv-decide-who">
              <span className="t1-rv-decide-name" title={name}>
                {name}
              </span>
              <span className="t1-rv-decide-sub">
                {report.interview?.score != null
                  ? `Interview ${report.interview.score}/100 · ${report.interview.label}`
                  : "Interview not scored yet"}
                {ev ? (composite != null ? ` · Competency ${composite}/100 · ${match!.label} · coverage ${Math.round((ev.coverage ?? 0) * 100)}%` : " · Competency: insufficient evidence to score") : report.evaluationPending ? " · Evaluation queued" : ""}
                {" · "}you decide — the engine ranks, it never rejects on its own
              </span>
            </div>
            <div className="t1-rv-decide-act">
              {isFulfillingVendor ? (
                <span className="pill n">The client makes the final decision</span>
              ) : settled ? (
                <span className={settled.outcome === "advanced" ? "pill v" : "pill n"}>
                  {settled.label}
                  {settled.at ? ` · ${new Date(settled.at).toLocaleDateString()}` : ""}
                </span>
              ) : readOnly ? (
                <span className="pill n">Read-only — decided by the hiring team, not from this link</span>
              ) : (
                <>
                  <button
                    className="btn primary"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ action: "advance" })}
                  >
                    <CheckBig /> {decide.isPending ? "Recording…" : "Advance"}
                  </button>
                  <button
                    className="btn"
                    disabled={updateStatus.isPending}
                    onClick={() => updateStatus.mutate("needs_review")}
                  >
                    Needs review
                  </button>
                  <button
                    className="btn danger"
                    disabled={decide.isPending}
                    onClick={() => {
                      decide.reset();
                      setRejectOpen(true);
                    }}
                  >
                    Reject…
                  </button>
                </>
              )}
            </div>
          </div>
          {report.session?.humanRequested && (
            <div className="banner warn" style={{ margin: 0 }}>
              <Alert />
              <div>
                <b>The candidate requested a human interview.</b> Reach out before deciding.
              </div>
            </div>
          )}
          {decideError && (
            <div className="banner crit" style={{ margin: 0 }}>
              <span>{decideError}</span>
            </div>
          )}
          {settled && (
            <div className="banner info" style={{ margin: 0 }}>
              <Info />
              <div>
                <b>{settled.label}.</b> This is the decision the server holds
                {settled.at ? ` (recorded ${new Date(settled.at).toLocaleString()})` : ""}
                {settled.reason ? ` — “${settled.reason}”` : ""}. A recorded decision can't be
                reversed from this screen{readOnly ? "" : "; ask your Owner if it has to be overturned"}. The full
                decision audit trail is in Trust &amp; Verification below.
              </div>
            </div>
          )}
          {isFulfillingVendor && (
            <div className="banner info" style={{ margin: 0 }}>
              <Users />
              <div>
                <b>The client makes the final hiring decision.</b> You're fulfilling this project —
                source and interview candidates; the end-customer reviews and decides.
              </div>
            </div>
          )}
          <EvaluationNotices
            evaluation={ev}
            lastFailed={report.lastFailedEvaluation ?? null}
            pending={report.evaluationPending ?? false}
          />

          {/* ══ 2. ONE TWO-COLUMN BODY. LEFT (stays in view): the recording and the team notes. RIGHT (scrolls): integrity and
                resume match, the COMPETENCY score, then the INTERVIEW ASSISTANT — which opens with the interview score and
                its recommendation and then lists every question. Two measurements, never blended; which one lists rank by
                is the organization's setting. */}
          <div className="t1-rv-evidence">
            <div className="t1-rv-evidence-side">
                <div className="card">
                  <div className="card-h">
                    <h3>Recording</h3>
                    <span className="t1-rv-rec-meta mut sm">
                      camera + mic · stored in-region ·{" "}
                      {report.recording?.expiresAt
                        ? recordingRetentionLabel(report.recording)
                        : "30-day retention"}
                      {recCover && (
                        <>
                          {/* NEVER imply a full recording unless the numbers say so: a partial one keeps an
                              amber icon and a visible word, with the detail on hover. */}
                          {recCover.verdict === "partial" && (
                            <InfoTip tone="warn" label="Partial recording">
                              <strong>This recording does not cover the whole session.</strong> {recCover.note}
                              {(report.recording?.partsDeleted ?? 0) > 0 &&
                                ` ${report.recording!.partsDeleted} earlier part${report.recording!.partsDeleted === 1 ? " has" : "s have"} already been deleted under the retention policy and cannot be played.`}
                            </InfoTip>
                          )}
                          {recCover.verdict !== "partial" && (
                            <InfoTip label="About this recording">
                              Camera + mic recording — play it back to review the candidate's answers exactly as given. {recCover.note}
                            </InfoTip>
                          )}
                          {report.recording?.expiresAt && (
                            <InfoTip tone={expiresSoon(report.recording.expiresAt) ? "warn" : undefined} label="Retention">
                              <strong>
                                Recording available until{" "}
                                {new Date(report.recording.expiresAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                              </strong>
                              {" — "}
                              {daysLeftLabel(report.recording.expiresAt)}. Interview recordings are biometric data and are permanently
                              deleted at the end of their retention window. Download it before then if you need to keep it.
                            </InfoTip>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="card-b">
                    {recCover ? (
                      <>
                        {/* Retention stays a visible banner only when the deadline is close — otherwise it is
                            the icon beside the title. */}
                        {report.recording?.expiresAt && expiresSoon(report.recording.expiresAt) && (
                          <div className="banner warn" style={{ marginBottom: 12 }}>
                            <Alert />
                            <div>
                              <strong>Recording is deleted soon</strong>
                              {" — "}
                              {daysLeftLabel(report.recording.expiresAt)}. Download it now if you need to keep it.
                            </div>
                          </div>
                        )}
                        <RecordingPlayer parts={recCover.playable} controlRef={recordingControl} onTime={(sec) => setPlayhead(Math.floor(sec))} />
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                          {recCover.playable.map((p, i) => (
                            <a key={p.url ?? i} className="btn sm ghost" href={p.url} target="_blank" rel="noreferrer" download>
                              <Video /> Download{recCover.playable.length > 1 ? ` part ${i + 1}` : " recording"}
                            </a>
                          ))}
                        </div>
                      </>
                    ) : report.recording?.available ? (
                      // Registered and still inside its window, but no playback URL could be signed
                      // (storage misconfigured). Say that, rather than "no recording" — the bytes
                      // exist and claiming otherwise would misinform a hiring decision.
                      <div className="banner warn" style={{ marginBottom: 0 }}>
                        <Alert />
                        <div>
                          A recording exists for this session but playback can't be signed right now.
                          {report.recording.expiresAt && (
                            <>
                              {" It is scheduled for deletion on "}
                              {new Date(report.recording.expiresAt).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                              {"."}
                            </>
                          )}{" "}
                          Contact support if you need it before then.
                        </div>
                      </div>
                    ) : report.recording?.purged || report.recording?.erased ? (
                      // The bytes are gone. Say so plainly and say why — a reviewer who can't play
                      // the video needs to know it was deleted on schedule, not that it's broken.
                      <div className="banner warn" style={{ marginBottom: 0 }}>
                        <Alert />
                        <div>
                          <strong>Recording deleted</strong>
                          {report.recording.purgedAt && (
                            <>
                              {" on "}
                              {new Date(report.recording.purgedAt).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </>
                          )}
                          {". "}
                          {report.recording.erased
                            ? "The candidate exercised their right to erasure, so the media was permanently shredded."
                            : "Its 30-day retention window ended and the media was permanently deleted."}{" "}
                          The transcript, scores and decision trail below are unaffected.
                          {report.recording.sha256 && (
                            <div className="mut sm" style={{ marginTop: 6 }}>
                              Content digest retained as proof the score was computed on those exact
                              bytes: <code>{report.recording.sha256.slice(0, 16)}…</code>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : report.redacted ? (
                      <div className="banner" style={{ marginBottom: 0 }}>
                        <ShieldCheck />
                        <div>
                          <strong>Recording withheld.</strong> This is a redacted share, so the candidate's name,
                          contact details, resume, recording and transcript are not included. Ask the sharing
                          organisation for an unredacted link if you need them.
                        </div>
                      </div>
                    ) : (
                      <div className="banner warn" style={{ marginBottom: 0 }}>
                        <Alert />
                        <div>
                          <strong>No recording was captured for this session</strong> — the candidate's
                          camera was off or never granted permission, so nothing could be recorded. The
                          interview still scores normally from the typed/spoken answers below. To get
                          recordings, ask candidates to allow camera &amp; mic on the check-in screen.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Team notes sit under the recording so the left column carries the reviewer's
                    working tools while the transcript runs down the right (audit 15). Internal: never
                    on a shared report. */}
                {!readOnly && <div className="card">
                  <div className="card-h">
                    <h3>Team notes</h3>
                    <span className="mut sm">private to your org</span>
                  </div>
                  <div className="card-b">
              {notesQuery.isLoading && <p className="mut sm">Loading notes…</p>}
              {notesQuery.isError && (
                <div className="banner crit" style={{ marginBottom: 10 }}>
                  <span>Couldn't load notes.</span>
                </div>
              )}
              {notes.map((n) => (
                <div key={n.id} style={{ padding: "9px 0", borderBottom: "1px solid var(--line-soft)" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                    {n.authorName ?? "Teammate"}{" "}
                    <span className="mut" style={{ fontWeight: 500 }}>
                      · {new Date(n.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p style={{ margin: "4px 0 0", fontSize: 12.8, lineHeight: 1.5 }}>{n.body}</p>
                </div>
              ))}
              {!notesQuery.isLoading && !notesQuery.isError && notes.length === 0 && (
                <p className="mut sm">No notes yet — what did your team think?</p>
              )}
              <div className="field" style={{ margin: "12px 0 8px" }}>
                <textarea
                  rows={3}
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  placeholder="Visible to your org only…"
                  maxLength={4000}
                />
              </div>
              {addNote.isError && (
                <div className="banner crit" style={{ marginBottom: 8 }}>
                  <span>Couldn't add the note. Please retry.</span>
                </div>
              )}
              <button
                className="btn sm primary"
                disabled={noteBody.trim().length === 0 || addNote.isPending}
                onClick={() => addNote.mutate()}
              >
                {addNote.isPending ? "Adding…" : "Add note"}
              </button>
            </div>
                </div>}
            </div>
            <div className="t1-rv-evidence-main t1-rv-stack">
              {/* Top row: the INTERVIEW SCORE. Integrity and the competency score follow the Interview assistant. */}
              <div className="t1-rv-pair solo">
              {report.interview && (
                <InterviewScorePanel
                  interview={report.interview}
                  rankingBasis={report.rankingBasis ?? "interview"}
                  candidacyId={report.candidacy.id}
                  listHref={isFulfillingVendor || readOnly ? undefined : `/app/candidates?reqId=${encodeURIComponent(report.candidacy.requisitionId)}`}
                  canScore={!readOnly && !isFulfillingVendor && report.session?.completedAt != null}
                  onQueued={() => void reportQuery.refetch()}
                />
              )}
              </div>
              <ResumeMatchCard report={report} isFulfillingVendor={isFulfillingVendor} onChanged={() => void reportQuery.refetch()} />
            <ConductObservations
              candidacyId={report.candidacy.id}
              events={report.integrity?.events ?? []}
              captured={(report.integrity?.events ?? []).length > 0}
              onSeek={(sec) => recordingControl.current?.seekTo(sec)}
            />
            {report.redacted ? (
              <div className="card pad mut sm">
                <b>Questions and answers withheld.</b> This is a redacted share — the transcript is not included.
                The interview score above lists each question's score and the reason for it.
              </div>
            ) : (
            <InterviewAssistant
              items={report.transcript ?? []}
              headline={provenanceHeadline(report.questionProvenance, (report.transcript ?? []).length) ?? `${(report.transcript ?? []).length} questions`}
              headerRight={
                (() => {
                        const canCopyQA = (report.transcript ?? []).length > 0 && (reqInfo?.flags?.copyQuestionsEnabled ?? true);
                        const canCopyAiPrompt =
                          !readOnly && !!reqInfo?.flags?.customPromptEnabled && !!me?.role && PROMPT_EDITOR_ROLES.has(me.role);
                        if (!canCopyQA && !canCopyAiPrompt) return null;
                        return (
                          <div style={{ position: "relative" }}>
                            <button
                              type="button"
                              className="btn sm ghost"
                              onClick={() => setCopyMenuOpen((v) => !v)}
                            >
                              <Copy /> Copy ▾
                            </button>
                            {copyMenuOpen && (
                              <>
                                {/* Click-outside catcher — a plain overlay, no library needed for one menu. */}
                                <div
                                  onClick={() => setCopyMenuOpen(false)}
                                  style={{ position: "fixed", inset: 0, zIndex: 90 }}
                                />
                                <div
                                  className="card"
                                  style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    right: 0,
                                    zIndex: 91,
                                    minWidth: 200,
                                    padding: 6,
                                    boxShadow: "var(--shadow)",
                                  }}
                                >
                                  {canCopyQA && (
                                    <button
                                      type="button"
                                      className="btn sm ghost"
                                      style={{ width: "100%", justifyContent: "flex-start" }}
                                      onClick={() => {
                                        setCopyMenuOpen(false);
                                        copyTranscript(report.transcript);
                                      }}
                                    >
                                      Questions &amp; answers only
                                    </button>
                                  )}
                                  {canCopyAiPrompt && (
                                    <button
                                      type="button"
                                      className="btn sm ghost"
                                      style={{ width: "100%", justifyContent: "flex-start" }}
                                      disabled={copyJudgePrompt.isPending}
                                      onClick={() => {
                                        setCopyMenuOpen(false);
                                        copyJudgePrompt.mutate();
                                      }}
                                    >
                                      Exact prompt sent to the AI judge
                                    </button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()
              }
              note={ev ? "Every answer was read against every criterion — one answer can be evidence for several competencies, listed under each answer with the quote. The question score is separate: it is scored from the role and that question alone and feeds the interview score, not a competency score." : undefined}
              controlRef={recordingControl}
              playheadSec={playhead}
              hasRecording={!!recCover && recCover.playable.length > 0}
              renderDetail={(t) => <AnswerDetail t={t} language={report.session?.language} />}
            />
            )}
            {/* After the assistant: integrity, then the competency score. */}
              <div className="card t1-rv-kpi">
                <div className="t1-rv-kpi-l">Integrity</div>
                <div className={`t1-rv-kpi-n t1-rv-int ${integrityTile.tone}`}>
                  <span style={{ display: "inline-flex", width: 20 }}>
                    {integrityTile.tone === "v" ? <Check /> : integrityTile.tone === "n" ? <ShieldCheck /> : <Alert />}
                  </span>
                  {integrityTile.label}
                </div>
                <div className="t1-rv-kpi-d">{integrityTile.detail}</div>
              </div>
          {!ev && (
            <div className="card pad" style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <p className="mut sm" style={{ margin: 0 }}>
                {report.session?.completedAt
                  ? "No competency evaluation yet — it fills in once the answers are evaluated. The recording and transcript below are complete."
                  : "Not evaluated yet — the report fills in once the interview is complete."}
              </p>
              {!readOnly && report.canReEvaluate && !report.evaluationPending && !isFulfillingVendor && (
                <ReEvaluateControl
                  candidacyId={report.candidacy.id}
                  latestId={report.evaluationHistory?.[0]?.id ?? null}
                  refetch={async () => {
                    const r = await reportQuery.refetch();
                    return { latest: r.data?.report.evaluationHistory?.[0]?.id ?? null };
                  }}
                />
              )}
            </div>
          )}

          {/* ══ 4. EVIDENCE — recording + team notes (left, sticky while short) beside the
                transcript (right, flows with the page). */}
          {ev && (
              <div className="card t1-rv-evalcard" style={{ minWidth: 0 }}>
                <div className="card-h">
                  <h3>Competency score</h3>
                  <span
                    title="Each competency is scored 0–100 from what the candidate actually said. Capability is the average of the competencies the interview could assess; competencies it could not assess are left out, not counted as zero. A human decides."
                    style={{ display: "inline-flex", width: 13, color: "var(--ink-mute)", cursor: "help" }}
                  >
                    <Info />
                  </span>
                </div>
                <div className="card-b">
                  {verdict && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "13px 16px",
                      borderRadius: 10,
                      marginBottom: 18,
                      background: `var(--${verdict.tone === "v" ? "good" : verdict.tone === "r" ? "warn" : "crit"}-wash)`,
                      color: `var(--${verdict.tone === "v" ? "good" : verdict.tone === "r" ? "warn" : "crit"})`,
                    }}
                  >
                    <span style={{ display: "inline-flex", width: 20, flex: "none" }}>
                      {verdict.tone === "v" ? <CheckBig /> : <Alert />}
                    </span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14.5 }}>{verdict.text}</div>
                      <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.5, color: "var(--ink)" }}>
                        {humaneRecommendation(ev.recommendation ?? "")}
                      </p>
                    </div>
                  </div>
                  )}
                  <div style={{ marginBottom: 14 }}>
                    <CapabilityCoverage e={ev} showLine={!verdict} />
                  </div>
                  <CompetencyEvidenceList
                    competencies={ev.competencies}
                    onQuestion={(seq) => document.getElementById(`rv-q-${seq}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  />
                  {evaluation.notShown.length > 0 && (
                    <p className="mut sm" style={{ margin: "10px 0 0" }}>
                      Not enough to judge from this interview: {evaluation.notShown.join(", ")}. Ask about them in a
                      follow-up conversation; they are not counted against the candidate.
                    </p>
                  )}
                  {!readOnly && report.canReEvaluate && !report.evaluationPending && !isFulfillingVendor && (
                  <div style={{ padding: 16 }}>
                    <ReEvaluateControl
                      candidacyId={report.candidacy.id}
                      latestId={report.evaluationHistory?.[0]?.id ?? null}
                      refetch={async () => {
                        const r = await reportQuery.refetch();
                        return { latest: r.data?.report.evaluationHistory?.[0]?.id ?? null };
                      }}
                    />
                  </div>
                )}
                </div>
              </div>
          )}
            </div>
          </div>

          {/* ══ 7. TRUST & VERIFICATION / AUDIT — everything a reviewer might want to double-check
                but doesn't need to see by default: advisory integrity flags, raw proof signals,
                identity/verification method, and the full decision audit trail. Collapsed so the
                main path (score → recommendation → competencies → decision → evidence) stays the
                page, not this. */}
          <details className="rq-collapse">
            <summary>
              <ShieldCheck />
              Trust &amp; Verification / Audit
              <span className="mut sm" style={{ fontWeight: 400 }}>
                — integrity signals, identity check, evaluation history
              </span>
              <Arrow className="chev" />
            </summary>
            <div className="body" style={{ display: "grid", gap: 16, paddingTop: 4 }}>
              <div className="grid g2" style={{ alignItems: "start" }}>
                <div>
                  {/* integrity flags — ADVISORY proctoring signals, never a score, never auto-decide */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-h">
                      <h3>Integrity flags</h3>
                      <span className="mut sm">advisory — for your review, not a score</span>
                    </div>
                    <div className="card-b">
                      {/* WHAT COULD BE SEEN — always shown, above the flags, because it is the sentence
                        that stops the same number meaning two different things. A candidate whose
                        camera was never read must not look like one we watched all the way through. */}
                      {presence && (
                        <div className="banner" style={{ marginBottom: flagSentences.length === 0 ? 12 : 14 }}>
                          <ShieldCheck />
                          <div>
                            <b>{presence.headline}</b>
                            <div className="sm" style={{ marginTop: 4, opacity: 0.85 }}>
                              {presence.detail}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* #43 Attention — its own block, never mixed into the flag sentences, because it
                        can NEVER appear without the fairness caveat attached. null = natural mode or
                        an older API payload: nothing renders, nothing is claimed. */}
                      {attention && (
                        <div
                          style={{
                            marginBottom: 14,
                            padding: "10px 12px",
                            border: "1px solid var(--line-soft)",
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 600, fontSize: 12.8, marginBottom: 4 }}>
                            <span style={{ display: "inline-flex", width: 15, flex: "none" }}>
                              <Eye />
                            </span>
                            Attention · measured only while speaking
                          </div>
                          {attention.measured ? (
                            <>
                              <div style={{ fontSize: 13.2 }}>
                                Looked away from the screen {attention.episodes}{" "}
                                {attention.episodes === 1 ? "time" : "times"} while speaking,{" "}
                                {attention.totalSeconds}s in total. Thinking time is free — nothing was
                                noted while the candidate was silent.
                              </div>
                              <div className="mut sm" style={{ marginTop: 6 }}>
                                Gaze estimation is approximate and varies with lighting, glasses, and
                                individual differences — including neurodivergence. Treat this as context
                                for a conversation, never as evidence on its own.
                              </div>
                            </>
                          ) : (
                            // Strict mode, but no gaze_watch_on proof — honest silence, not "all clear".
                            <div className="mut sm">Attention was not measured.</div>
                          )}
                        </div>
                      )}
                      {/* #43 Strict session timeline — pause → resume → bypass, in order. Prominent so a
                        human READS it, but every line is mechanics ("what happened"), not verdicts:
                        strict mode pauses and waits, it never ends an interview or fails anyone. */}
                      {strictTimeline.length > 0 && (
                        <div
                          style={{
                            marginBottom: 14,
                            padding: "10px 12px",
                            border: "1px solid var(--line-soft)",
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 600, fontSize: 12.8, marginBottom: 2 }}>
                            <span style={{ display: "inline-flex", width: 15, flex: "none" }}>
                              <Clock />
                            </span>
                            Strict session timeline
                          </div>
                          <div className="mut sm" style={{ marginBottom: 6 }}>
                            Strict mode pauses and invites the candidate back — it never ends an
                            interview. What happened, in order:
                          </div>
                          {(showAllTimeline ? strictTimeline : strictTimeline.slice(0, 3)).map((e, i) => {
                            const resumed = e.kind === "strict_resume" ? parseStrictResume(e.detail) : null;
                            const line =
                              e.kind === "strict_pause"
                                ? `Paused — ${strictReasonLabel(e.detail)}`
                                : e.kind === "strict_resume"
                                  ? resumed
                                    ? `Resumed after ${resumed.seconds}s (${strictReasonLabel(resumed.reason)})`
                                    : "Resumed"
                                  : // Bypass is the escape hatch WORKING, not a violation — a broken
                                  // webcam must never dead-end an honest candidate, so the copy is
                                  // neutral by construction.
                                  `Continued without ${bypassRequirementLabel(e.detail)} after a technical issue — noted, not penalised`;
                            return (
                              <div key={i} className="krow">
                                <span className="k" style={{ fontSize: 12.8 }}>
                                  {line}
                                </span>
                                <span className="v mut sm">{new Date(e.at).toLocaleString()}</span>
                              </div>
                            );
                          })}
                          {strictTimeline.length > 3 && (
                            <button
                              className="btn sm secondary"
                              style={{ marginTop: 8 }}
                              onClick={() => setShowAllTimeline(!showAllTimeline)}
                            >
                              {showAllTimeline ? "Hide timeline" : `Show all (${strictTimeline.length})`}
                            </button>
                          )}
                        </div>
                      )}
                      {flagSentences.length === 0 ? (
                        <div className="banner info" style={{ marginBottom: 0 }}>
                          <Check />
                          <div>No integrity flags — clean session.</div>
                        </div>
                      ) : (
                        <>
                          <div className="banner info" style={{ marginBottom: 14 }}>
                            <Info />
                            <div>
                              These are <b>advisory signals</b> the browser observed during the interview — context
                              for you to weigh. They are <b>not</b> a score and never reject a candidate on their own.
                            </div>
                          </div>
                          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                            {flagSentences.map((sentence, i) => (
                              <li
                                key={i}
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  alignItems: "center",
                                  padding: "9px 12px",
                                  border: "1px solid var(--line-soft)",
                                  borderRadius: 8,
                                  fontSize: 13.2,
                                }}
                              >
                                <span style={{ display: "inline-flex", width: 15, color: "var(--warn)", flex: "none" }}>
                                  <Alert />
                                </span>
                                <span>{sentence}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {/* The raw event tail renders whenever events exist AT ALL — gaze and strict
                        events produce no flag sentence, and hiding their receipts behind the flag
                        list would orphan exactly the rows the blocks above summarise. */}
                      {(report.integrity?.events ?? []).length > 0 && (
                        <details style={{ marginTop: 12 }}>
                          <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
                            Event timeline ({report.integrity?.events.length})
                          </summary>
                          <div style={{ marginTop: 8 }}>
                            {(report.integrity?.events ?? []).map((e, i) => (
                              <div key={i} className="krow">
                                <span className="k">
                                  <span className="pill n">{INTEGRITY_EVENT_LABEL[e.kind] ?? e.kind}</span>{" "}
                                  {e.detail && (
                                    <span className="sm mut">{integrityEventDetail(e.kind, e.detail)}</span>
                                  )}
                                </span>
                                <span className="v mut sm">{new Date(e.at).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>

                  {/* integrity & proof */}
                  <div className="card">
                    <div className="card-h">
                      <h3>Integrity &amp; proof</h3>
                      <span className="mut sm">proof only — recomputed server-side, never a judgment</span>
                    </div>
                    <div className="card-b">
                      <div className="grid g2">
                        {(integ?.signals ?? []).map((sig, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              gap: 10,
                              alignItems: "flex-start",
                              padding: "10px 12px",
                              border: "1px solid var(--line-soft)",
                              borderRadius: 8,
                            }}
                          >
                            <span
                              style={{
                                display: "grid",
                                placeItems: "center",
                                width: 30,
                                height: 30,
                                borderRadius: 7,
                                flex: "none",
                                background: sig.verdict === "pass" ? "var(--good-wash)" : sig.verdict === "flag" ? "var(--warn-wash)" : "var(--crit-wash)",
                                color: sig.verdict === "pass" ? "var(--good)" : sig.verdict === "flag" ? "var(--warn)" : "var(--crit)",
                              }}
                            >
                              <span style={{ display: "inline-flex", width: 15 }}>
                                {sig.verdict === "pass" ? <Check /> : <Alert />}
                              </span>
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.8 }}>
                                {SIGNAL_LABEL[sig.type] ?? sig.type}
                              </div>
                              <div className="mut" style={{ fontSize: 11.3 }}>
                                {sig.detector} v{sig.detectorVersion} · confidence {Math.round(sig.confidence * 100)}%
                              </div>
                            </div>
                            <span className={VERDICT_PILL[sig.verdict].cls}>{VERDICT_PILL[sig.verdict].label}</span>
                          </div>
                        ))}
                      </div>
                      {(integ?.signals ?? []).length === 0 && (
                        <p className="mut sm" style={{ margin: 0 }}>
                          Integrity signals arrive with the authoritative post-hoc pass.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  {/* verification */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-h">
                      <h3>Verification</h3>
                      <span style={{ display: "inline-flex", width: 15, color: "var(--teal-ink)" }}>
                        <ShieldCheck />
                      </span>
                    </div>
                    <div className="card-b">
                      {report.verification ? (
                        <>
                          <div className="krow">
                            <span className="k">Identity + liveness</span>
                            <span className="v">
                              {/* An email OTP is inbox-control proof, NOT identity — show a neutral
                                "Email confirmed", never a green identity tick. "disabled" means the
                                check never ran (neutral "Off"), not a red "Not verified" that reads
                                as the candidate failing something. */}
                              {report.verification.verified ? (
                                <span className="pill v">Verified</span>
                              ) : report.verification.emailConfirmed ? (
                                <span className="pill n">Email confirmed · not an ID check</span>
                              ) : report.verification.method === "disabled" ? (
                                <span className="pill n">Off for this interview</span>
                              ) : (
                                <span className="pill f">Not verified</span>
                              )}
                            </span>
                          </div>
                          <div className="krow">
                            <span className="k">Method</span>
                            <span className="v" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                              {report.verification.method ?? "—"}
                            </span>
                          </div>
                          <div className="krow">
                            <span className="k">Checked</span>
                            <span className="v sm">{new Date(report.verification.createdAt).toLocaleString()}</span>
                          </div>
                        </>
                      ) : (
                        <p className="mut sm" style={{ margin: 0 }}>
                          Identity check runs at candidate check-in.
                        </p>
                      )}
                      {report.session && (
                        <>
                          <div className="krow">
                            <span className="k">Scheduled</span>
                            <span className="v sm">
                              {report.session.scheduledAt ? new Date(report.session.scheduledAt).toLocaleString() : "—"}
                            </span>
                          </div>
                          <div className="krow">
                            <span className="k">Completed</span>
                            <span className="v sm">
                              {report.session.completedAt ? new Date(report.session.completedAt).toLocaleString() : "—"}
                            </span>
                          </div>
                          {report.session.language && report.session.language !== "en" && (
                            <div className="krow">
                              <span className="k">Language</span>
                              <span className="v sm">{languageFullLabel(report.session.language)}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* evaluation history — the decision audit trail (advance/reject/override, who,
                      when, why). The CURRENT decision is still surfaced inline in Hiring Decision
                      above (the "settled" banner) so acting on a candidate never requires opening
                      this section first; this is the full record for when someone needs it. */}
                  <div className="card">
                    <div className="card-h">
                      <h3>Decision history</h3>
                      <span className="mut sm">decision audit trail</span>
                    </div>
                    <div className="card-b">
                      {report.decisions.length === 0 ? (
                        <p className="mut sm" style={{ margin: 0 }}>
                          No decision has been recorded for this candidacy yet.
                        </p>
                      ) : (
                        report.decisions.map((d, i) => (
                          <div key={i} className="krow">
                            <span className="k">
                              <span className={d.action === "advance" ? "pill v" : d.action === "override" ? "pill pv" : "pill r"}>
                                {d.action.replace(/_/g, " ")}
                              </span>{" "}
                              <span className="sm">
                                {d.provenance === "human" ? "human" : "auto"} · {d.reason || "—"}
                                {d.assessmentSetVersion != null ? ` · criteria v${d.assessmentSetVersion}` : ""}
                              </span>
                            </span>
                            <span className="v mut sm" style={{ fontWeight: 500 }}>
                              {new Date(d.ts).toLocaleString()}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="card" style={{ marginTop: 16 }}>
                    <div className="card-h">
                      <h3>Evaluation history</h3>
                      <span className="mut sm">every evaluation of this interview — failures never replace the current one</span>
                    </div>
                    <div className="card-b">
                      <EvaluationHistory items={report.evaluationHistory ?? []} />
                      {ev && (
                        <p className="mut sm" style={{ margin: "10px 0 0" }}>
                          Current result: {ev.provenance.judgeModelResolved ?? ev.provenance.judgeModel ?? "—"} · prompt{" "}
                          <code>{ev.provenance.promptSha256?.slice(0, 12) ?? "—"}</code> · read the{" "}
                          {ev.provenance.textSource === "recording" ? "recording-verified" : "captured"} transcript.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="disclaimer" style={{ margin: 0 }}>
                <b>Facial AI is proof only</b> — identity, liveness, injection &amp; second-person checks; it never
                scores face, tone, affect or personality. Every competency score cites verbatim evidence from the answers; a competency without evidence is marked insufficient, never scored. Rejection
                is human-gated and audited.
              </div>
            </div>
          </details>
        </div>
      )}

      {/* ---- reject modal (gated) ---- */}
      {rejectOpen && (
        <Modal title={`Reject ${name}?`} onClose={closeReject}>
          <p className="mut sm" style={{ marginTop: 0 }}>
            The engine ranks, but it never sends a rejection on its own. This is logged with your reason for
            the audit trail, and the candidate is notified per your org's policy.
          </p>
          <div className="field">
            <label htmlFor="rej-reason">
              Reason <span className="hint">— required, audited</span>
            </label>
            <textarea
              id="rej-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Stronger candidates advanced / below the bar on a must-have competency"
              maxLength={1000}
            />
          </div>
          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 12.8, marginBottom: 14, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={rejectConfirmed}
              onChange={(e) => setRejectConfirmed(e.target.checked)}
              style={{ marginTop: 2, width: 15, height: 15, accentColor: "var(--crit)" }}
            />
            <span>
              I'm a human making this decision — I've reviewed the report and take responsibility for this
              rejection.
            </span>
          </label>
          {decideError && (
            <div className="banner crit" style={{ marginBottom: 12 }}>
              <span>{decideError}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="btn" onClick={closeReject}>
              Cancel
            </button>
            <button
              className="btn danger-solid"
              disabled={
                rejectReason.trim().length < 4 || !rejectConfirmed || decide.isPending || !!settled
              }
              onClick={() => decide.mutate({ action: "reject", reason: rejectReason.trim() })}
            >
              {decide.isPending ? "Logging…" : "Confirm & log rejection"}
            </button>
          </div>
        </Modal>
      )}

      {/* ---- share modal ---- */}
      {logOpen && report && (
        <Modal title={`Activity log — ${name}`} onClose={() => setLogOpen(false)} maxWidth={880}>
          <CandidateTimeline candidacyId={report.candidacy.id} />
        </Modal>
      )}
      {sendConfirmOpen && report && (
        <ConfirmChargeModal
          title="Send interview"
          subtitle={`Email ${name} their voice AI interview link for ${report.requisition.title}.`}
          lines={[{ label: "Candidate", value: report.candidateEmail ? `${name} · ${report.candidateEmail}` : `${name} (no email — you'll get a link to share)` }]}
          unit="interview"
          count={1}
          confirmLabel={sendInterview.isPending ? "Sending…" : "Send interview"}
          busy={sendInterview.isPending}
          error={
            sendInterview.isError
              ? sendInterview.error instanceof ApiError && sendInterview.error.status === 402
                ? "Not enough AI interviews left on your plan — nothing was charged."
                : sendInterview.error instanceof Error ? sendInterview.error.message : "Couldn't send the interview."
              : null
          }
          onConfirm={() => sendInterview.mutate(report.candidacy.requisitionId)}
          onCancel={() => setSendConfirmOpen(false)}
        />
      )}
      {interviewLink && (
        <Modal title="Interview link" onClose={() => setInterviewLink(null)}>
          <p className="sm" style={{ marginTop: 0 }}>
            {interviewLink.fresh
              ? "A new link for this candidate. The earlier link no longer works — share this one with them."
              : interviewLink.emailedTo
                ? `Interview sent — emailed to ${interviewLink.emailedTo}. You can also share the link yourself.`
                : "Interview created. There's no email on file, so share this link with the candidate yourself."}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <input className="input" readOnly value={interviewLink.url} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1 }} />
            <button className="btn primary" type="button" onClick={() => void copyInterviewLink(interviewLink.url)}>
              {linkCopied ? <Check /> : <Copy />} {linkCopied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="mut sm" style={{ marginBottom: 0 }}>Valid for 7 days. Only this candidate's interview opens with it.</p>
        </Modal>
      )}
      {shareOpen && (
        <Modal title={`Share ${name}'s report`} onClose={() => setShareOpen(false)}>
          {!shareUrl && (
            <>
              <p className="mut sm" style={{ marginTop: 0 }}>
                Creates a revocable, expiring read-only link. Redaction hides the candidate's name and the
                transcript — on by default for external recipients.
              </p>
              <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 12.8, marginBottom: 14, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={shareRedacted}
                  onChange={(e) => setShareRedacted(e.target.checked)}
                  style={{ marginTop: 2, width: 15, height: 15, accentColor: "var(--teal)" }}
                />
                <span>
                  <b>Redact candidate PII</b> — name hidden, transcript excluded (recommended for external
                  shares).
                </span>
              </label>
              <div className="field">
                <label htmlFor="share-email">
                  Email the link <span className="hint">— optional; we'll send it for you</span>
                </label>
                <input
                  id="share-email"
                  className="input"
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="name@company.com"
                />
              </div>
              {!shareRedacted && (
                <div className="banner warn" style={{ marginBottom: 12 }}>
                  <Alert />
                  <div>
                    <b>Unredacted share.</b> The recipient sees the candidate's name and full transcript.
                  </div>
                </div>
              )}
              {share.isError && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <span>Couldn't create the share link. Please retry.</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button className="btn" onClick={() => setShareOpen(false)}>
                  Cancel
                </button>
                <button className="btn primary" disabled={share.isPending} onClick={() => share.mutate()}>
                  {share.isPending ? "Creating…" : "Create share link"}
                </button>
              </div>
            </>
          )}
          {shareUrl && (
            <>
              <div className="banner info" style={{ marginBottom: 12 }}>
                <CheckBig />
                <div>
                  <b>Link created{shareEmail.trim() ? ` and emailed to ${shareEmail.trim()}` : ""}.</b> It's
                  shown once — copy it now.
                </div>
              </div>
              <code style={{ display: "block", fontFamily: "var(--mono)", fontSize: 12, wordBreak: "break-all", padding: "10px 12px", background: "var(--surface-2)", borderRadius: 8, marginBottom: 12 }}>
                {shareUrl}
              </code>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button className="btn" onClick={() => setShareOpen(false)}>
                  Done
                </button>
                <button className="btn primary" onClick={() => copyShare(shareUrl)}>
                  {copied ? "Copied ✓" : "Copy link"}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}
      {toast && (
        <div
          className={`banner ${toast.tone === "good" ? "ok" : "warn"}`}
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 200,
            maxWidth: 360,
            boxShadow: "var(--shadow)",
          }}
        >
          <span>{toast.text}</span>
        </div>
      )}
    </section>
  );
}

